import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Link,
  Paper,
  keyframes,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Marquee from "react-fast-marquee";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./LandingPage.css";
import UserInfoForm from "./UserInfoForm";
import axios from "axios";

localStorage.setItem("answersSubmitted", "false");

const navItems = [
  { label: "AskCarol", href: "#askcarol" },
  { label: "Why Choose Coach Carol", href: "#why-choose" },
  { label: "Career Resources", href: "#resources" },
];

const lightColors = ["#FFFDD0", "#FDF5E6"];

const heroContent = {
  titleLine1: "Unlock Your",
  titleLine2: "Career Wellness",
  description:
    "Let CoachCarol™ AI analyze your career wellness, happiness, strategy, and agility to create a personalized development plan.",
  ctaText: "LET'S GET STARTED",
  imageOverview: "/images/landing-page-new/banner_header.png",
  // ctaIcon: "/images/landing-page-new/icon-cta-hero.svg",
};

const ASK_CAROL_TITLE_VARIATIONS = [
  "Unleash Your Career Today - Tailored Career Advice for Singapore's PMETs",
  "Navigate Your Career Crossroads with Confidence - Start Your Personalized Assessment Now!",
  "Accelerate Your Career Growth - Discover Your Personalized Strategy Today!",
  "Reinvent Your Career - Begin Your Career Journey with CoachCAROL™",
  "Stay Ahead in the Competitive Job Market - Get Your Personalized Career Plan Now!",
  "Get all your Career Questions answered  - Let our AI-powered Coach show the way!",
];

const askCarolContent = {
  title: "Let Me Answer Your Toughest Career Questions",
  description:
    "Meet CoachCAROL™, your hyper-personalized Digital Career Coach, helping your everyday Career challenges with practical advice 24/7",
  videoUrl:
    "https://player.cloudinary.com/embed/?cloud_name=dncu0dqbk&public_id=Adrian_Choo_s_Video_-_May_13_2025-VEED_1_zzjs29&player[pictureInPictureToggle]=true&player[hideContextMenu]=true&player[floatingWhenNotVisible]=right&player[posterOptions][transformation][start_offset]=1",
  bulletPoints: [
    "Is my Career heading in the right direction?",
    "How do I get promoted?",
    "Should I quit my job?",
    "What are my career blind-spots?",
    "How do I ask for a raise?",
  ],
};

const benefits = [
  {
    title: "Career Wellness Analysis",
    description:
      "Gain personalized insights from our AI-Driven diagnostic tool with customised ideas designed to improve your career.",
    image: "/images/landing-page-new/work-content-3.png",
    bgColor: "#E89FB2",
  },
  {
    title: "Career Avatar Profile",
    description:
      "Discover your unique Career Avatar and get tailored advice based on your Career profile.",
    image: "/images/landing-page-new/benefit-avatar-profile.png",
    bgColor: "#F2B994",
  },
  {
    title: "Personalised Career Scorecard",
    description:
      "Receive a comprehensive and objective assessment of your Career strengths and weaknesses, enabling data-driven decisions for professional growth.",
    image: "/images/landing-page-new/benefit-scorecard.png",
    bgColor: "#63CBCC",
  },
  {
    title: "Job Hunting Kit",
    description:
      "Access powerful tools to enhance your Job Search, e.g. Salary Negotiation Guides, Target Market Maps, and many more.",
    image: "/images/landing-page-new/benefit-job-kit.png",
    bgColor: "#F2B994",
  },
  {
    title: "Resume Resources",
    description:
      "Unlock a growing collection of actual Resume Samples that match your industry and role for easy reference when developing your CV.",
    image: "/images/landing-page-new/benefit-resume-resources.png",
    bgColor: "#63CBCC",
  },
  {
    title: "Online Video Courses",
    description:
      "Leverage the Power of Online Learning with a library of videos on Mid-Career Switching and Effective Job-Hunting Secrets.",
    image: "/images/landing-page-new/benefit-video-courses.png",
    bgColor: "#E89FB2",
  },
];

const testimonials = [
  // ... testimonials array (currently commented out in JSX) ...
];

const companyLogos = [
  "/images/logo/landingpagecorp/grab.png",
  "/images/logo/landingpagecorp/OCBC.png",
  "/images/logo/landingpagecorp/fluence.png",
  "/images/logo/landingpagecorp/hsbc.png",
  "/images/logo/landingpagecorp/scheider.png",
  "/images/logo/landingpagecorp/merck.png",
];

const resumeLibraryContent = {
  title: "Use our Library of Actual Industry/Role Specific Resume Samples",
  description:
    "Browse real-world resume samples tailored to specific industries and roles—designed to help you craft a resume that gets noticed.",
  tags: [
    "Banking & Finance",
    "FMCG",
    "Pharmaceutical",
    "Supply Chain",
    "Human Resources",
    "Corporate Communications",
    "Business Support Management",
    "Marketing",
    "Sales",
    "Software Engineering",
    "and many more...",
  ],
  icon: "/images/landing-page-new/icon-tick-green.svg",
  illustration: "/images/landing-page-new/resume_stitch.svg",
};

const careerResourcesContent = {
  titleLine1: "Access A Growing Library of",
  titleLine2: "Career Resources",
  description:
    "Check out cheatsheets and video courses to boost your skills and career confidence.",
  tags: [
    {
      icon: "/images/landing-page-new/icon-negotiation.svg",
      label: "Negotiation Guide",
    },
    {
      icon: "/images/landing-page-new/icon-linkedin.svg",
      label: "LinkedIn Branding",
    },
    {
      icon: "/images/landing-page-new/icon-network.svg",
      label: "Network Mapping",
    },
    {
      icon: "/images/landing-page-new/icon-interview.svg",
      label: "Interview Preparation",
    },
    {
      icon: "/images/landing-page-new/icon-salary-calc.svg",
      label: "Salary Calculation",
    },
  ],
  illustration: "/images/landing-page-new/illustration-career-resources.svg",
};

const finalCtaContent = {
  title: "Ready to Understand Your Career Health?",
  description:
    "Curious which career animal you are? Take the CoachCarol™ quiz and join thousands who've uncovered their career wellness scores and personal growth paths.",
  ctaText: "LET'S GET STARTED",
  // ctaIcon: "/images/landing-page-new/icon-cta-final.svg",
  animalIllustration: "/images/landing-page-new/career_animals_together.svg",
};

const figmaFontStyles = {
  merriweather700: {
    fontFamily: "Merriweather, serif",
    fontWeight: 700,
  },
  poppins400: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
  },
  poppins500: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 500,
  },
  poppins700: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 700,
  },
};

const bounceAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const SquigglyArrow = ({ sx = {} }) => (
  <Box
    sx={{
      position: "absolute",
      animation: `${bounceAnimation} 2s infinite ease-in-out`,
      ...sx,
    }}
  >
    <svg
      width="80"
      height="60"
      viewBox="0 0 80 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 25 C15 15, 35 35, 45 25 C55 15, 65 35, 72 25 L64 16 L78 25 L64 34"
        stroke="#DF7468"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAskCarolTitle, setSelectedAskCarolTitle] = useState(
    askCarolContent.title
  );
  const [showUserInfoForm, setShowUserInfoForm] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [otpTimer, setOtpTimer] = useState(null);

  // Effect for A/B testing title variations
  useEffect(() => {
    const variantStorageKey = "askCarolTitleVariantIndex";
    const clarityTagKey = "askCarolTitleVariantName";
    let chosenIndex;

    const storedIndexStr = localStorage.getItem(variantStorageKey);

    if (storedIndexStr) {
      const parsedIndex = parseInt(storedIndexStr, 10);
      if (
        !isNaN(parsedIndex) &&
        parsedIndex >= 0 &&
        parsedIndex < ASK_CAROL_TITLE_VARIATIONS.length
      ) {
        chosenIndex = parsedIndex;
      }
    }

    if (typeof chosenIndex === "undefined") {
      chosenIndex = Math.floor(
        Math.random() * ASK_CAROL_TITLE_VARIATIONS.length
      );
      localStorage.setItem(variantStorageKey, chosenIndex.toString());
    }

    const currentVariationTitle = ASK_CAROL_TITLE_VARIATIONS[chosenIndex];
    setSelectedAskCarolTitle(currentVariationTitle);

    if (window.clarity) {
      window.clarity(
        "set",
        clarityTagKey,
        `AskCarolTitle_Variation${chosenIndex + 1}`
      );
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Clean up loading state when component unmounts
  useEffect(() => {
    return () => {
      // If user navigates away without completing the transition, clear the loading state
      if (isLoading) {
        setIsLoading(false);
      }
    };
  }, [isLoading]);

  const handleStart = () => {
    if (isLoading) return;
    setShowUserInfoForm(true);
  };

  // OTP and user info logic (adapted from QuestionList)
  const handleSendOtp = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/send-otp`,
        { email: userInfo.email }
      );
      if (response.data.success) {
        setOtpSent(true);
        setErrorMessage("");
        setOtpTimer(
          setTimeout(() => {
            setErrorMessage("OTP expired. Please request a new one.");
            setOtpSent(false);
            setOtp("");
          }, 300000)
        ); // 5 minutes
      } else {
        setErrorMessage("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Error sending OTP. Please try again.");
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/verify-otp`,
        { email: userInfo.email, otp }
      );
      if (response.data.success) {
        setOtpVerified(true);
        clearTimeout(otpTimer);
        setSubmitting(true);
        const savedUser = await saveUserInfo();
        if (savedUser && savedUser.id) {
          navigate(`/dashboard/${savedUser.id}`);
        }
        return true;
      } else {
        setErrorMessage("Invalid OTP. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Error verifying OTP. Please try again.");
      setSubmitting(false);
    }
  };

  const saveUserInfo = async () => {
    setSubmitting(true);
    try {
      const userResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/users`,
        userInfo
      );
      const userId = userResponse.data.id;
      return { id: userId, ...userInfo };
    } catch (error) {
      setErrorMessage("There was an error submitting your information!");
    } finally {
      setSubmitting(false);
    }
  };

  const renderTag = (label, iconSrc, index, sx = {}) => (
    <Paper
      key={index}
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: "white",
        px: { xs: 2, md: "29px" },
        py: "12px",
        borderRadius: "40px",
        boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.15)",
        gap: "12px",
        ...sx,
      }}
    >
      <img src={iconSrc} alt="" style={{ height: "16px", width: "16px" }} />
      <Typography
        sx={{
          ...figmaFontStyles.poppins400,
          fontSize: "16px",
          lineHeight: "30px",
          color: "#333333",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Paper>
  );

  const renderMetric = (label, iconSrc, index) => (
    <Box
      key={index}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "15px",
        flexBasis: { xs: "100%", sm: "45%", md: "auto" },
        mb: { xs: 1.5, md: 0 },
      }}
    >
      <img src={iconSrc} alt="" style={{ height: "24px", width: "24px" }} />
      <Typography
        sx={{
          ...figmaFontStyles.poppins400,
          fontSize: "16px",
          lineHeight: "17px",
          color: "#333333",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography
          sx={{
            ...figmaFontStyles.poppins500,
            color: "#fff",
            textAlign: "center",
          }}
        >
          Loading your Dashboard...
        </Typography>
      </Backdrop>

      {showUserInfoForm ? (
        <UserInfoForm
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          handleSendOtp={handleSendOtp}
          handleVerifyOtp={handleVerifyOtp}
          otpSent={otpSent}
          otpVerified={otpVerified}
          otp={otp}
          setOtp={setOtp}
          errorMessage={errorMessage}
          submitting={submitting}
        />
      ) : (
        <>
          <AppBar
            position="static"
            elevation={0}
            sx={{ bgcolor: "#FFFFFF", borderBottom: "1px solid #eee" }}
          >
            <Container maxWidth="lg">
              <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={() => navigate("/")}
                  sx={{ mr: 2 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        ...figmaFontStyles.merriweather700,
                        fontSize: { xs: "1.8rem", md: "32px" },
                        lineHeight: 1.1,
                        color: "#000000",
                      }}
                    >
                      Coach Carol
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        ...figmaFontStyles.poppins400,
                        fontSize: "12px",
                        color: "#000000",
                      }}
                    >
                      Powered by Career Agility
                    </Typography>
                  </Box>
                </IconButton>

                {/* <Box sx={{ display: { xs: "none", md: "flex" }, gap: "50px" }}>
                  {navItems.map((item) => (
                    <Link
                      key={item.label}
                      href={item.href}
                      underline="none"
                      sx={{
                        ...figmaFontStyles.poppins400,
                        fontSize: "18px",
                        lineHeight: "15px",
                        color: "#000000",
                        cursor: "pointer",
                        "&:hover": {
                          color: "#DF7468",
                        },
                      }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </Box> */}

                <Button
                  variant="outlined"
                  onClick={() => navigate("/login")}
                  sx={{
                    borderRadius: "24px",
                    px: 4,
                    py: 1,
                    borderColor: "#000000",
                    color: "#000000",
                    ...figmaFontStyles.poppins400,
                    fontSize: "14px",
                    "&:hover": {
                      borderColor: "#000000",
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Login
                </Button>
              </Toolbar>
            </Container>
          </AppBar>

          <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#F1F5F8" }}>
            <Container maxWidth="lg">
              <Grid
                container
                spacing={6}
                alignItems="center"
                direction={{ xs: "column-reverse", md: "row" }}
              >
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      component="span"
                      sx={{
                        ...figmaFontStyles.merriweather700,
                        fontSize: { xs: "1.8rem", md: "50px" },
                        lineHeight: 1.1,
                        color: "#000000",
                      }}
                    >
                      {selectedAskCarolTitle}
                    </Typography>
                    <img
                      src="/images/landing-page-new/CoachCAROL-Header-Image.png"
                      alt="CoachCarol Avatar"
                      style={{
                        width: "150px",
                        height: "150px",
                        objectFit: "contain",
                        marginLeft: "10px",
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      ...figmaFontStyles.poppins400,
                      fontSize: "20px",
                      lineHeight: 1.5,
                      color: "#333333",
                      mb: 4,
                      maxWidth: "500px",
                    }}
                  >
                    {askCarolContent.description}
                  </Typography>
                  <Box
                    sx={{
                      mb: 0,
                      color: "#333333",
                      ...figmaFontStyles.poppins400,
                      fontSize: "18px",
                      lineHeight: 1.7,
                      maxWidth: "500px",
                    }}
                  >
                    {askCarolContent.bulletPoints.map((point, idx) => (
                      <Box
                        key={idx}
                        sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
                      >
                        <CheckCircleIcon
                          sx={{ color: "#4CAF50", fontSize: 24, mr: 1 }}
                        />
                        <span>{point}</span>
                      </Box>
                    ))}
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        mt: 3,
                        borderRadius: "5px",
                        px: "24px",
                        py: "12px",
                        fontSize: "16px",
                        fontWeight: 700,
                        textTransform: "none",
                        boxShadow: "none",
                        backgroundColor: "#DF7468",
                        "&:hover": { backgroundColor: "#DF7468", opacity: 0.9 },
                      }}
                      aria-label="Ask CoachCAROL Now!"
                      tabIndex={0}
                      onClick={handleStart}
                    >
                      Let's Get Started!
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{ textAlign: "center", maxWidth: "450px", mx: "auto" }}
                  >
                    <iframe
                      src={askCarolContent.videoUrl}
                      title="CoachCarol Introduction Video"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      style={{
                        width: "100%",
                        aspectRatio: "16/9",
                        border: "none",
                        borderRadius: "16px",
                        minHeight: "500px",
                        maxHeight: "600px",
                        background: "#000",
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Trusted by companies section */}

          <Box
            sx={{
              py: 8,
              bgcolor: "#FFFFFF",
            }}
          >
            <Typography
              textAlign="center"
              sx={{
                ...figmaFontStyles.merriweather700,
                fontSize: { xs: "2.5rem", md: "60px" },
                lineHeight: 1.167,
                color: "#000000",
                mb: 8,
              }}
            >
              Trusted by Leading Companies
            </Typography>
            <Marquee gradient={false} speed={40}>
              {companyLogos.map((logo, index) => (
                <Box
                  key={index}
                  sx={{
                    mx: { xs: 4, md: 6 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "150px",
                  }}
                >
                  <img
                    src={logo}
                    alt="Company Logo"
                    style={{
                      maxHeight: "100%",
                      maxWidth: "300px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ))}
            </Marquee>
          </Box>

          {/* Why Choose CoachCarol™ section */}

          <Box sx={{ py: { xs: 8, md: 15 } }} id="why-choose">
            <Container maxWidth="lg">
              <Typography
                textAlign="center"
                sx={{
                  ...figmaFontStyles.merriweather700,
                  fontSize: { xs: "2.5rem", md: "60px" },
                  lineHeight: 1.167,
                  color: "#000000",
                  mb: 8,
                }}
              >
                Why Choose CoachCarol™
              </Typography>
              <Grid container spacing={4}>
                {benefits.map((benefit, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: "5px",
                        bgcolor: benefit.bgColor,
                        boxShadow: "none",
                      }}
                    >
                      <CardContent sx={{ p: "28px 25px" }}>
                        <Box
                          sx={{
                            mb: 3,
                            height: "200px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={benefit.image}
                            alt={benefit.title}
                            style={{
                              maxHeight: "100%",
                              maxWidth: "100%",
                              objectFit: "contain",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            ...figmaFontStyles.poppins700,
                            fontSize: "30px",
                            lineHeight: 1.167,
                            color: "#41444B",
                            mb: 1,
                          }}
                        >
                          {benefit.title}
                        </Typography>
                        <Typography
                          sx={{
                            ...figmaFontStyles.poppins500,
                            fontSize: "16px",
                            lineHeight: 1.5,
                            color: "#41444B",
                          }}
                        >
                          {benefit.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                my: 4,
              }}
            >
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <SquigglyArrow sx={{ top: 10, left: -90 }} />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStart}
                  disableElevation
                  startIcon={
                    <img
                      src={finalCtaContent.ctaIcon}
                      alt=""
                      style={{ height: "14px" }}
                    />
                  }
                  sx={{
                    bgcolor: "#DF7468",
                    color: "#FFFFFF",
                    borderRadius: "5px",
                    px: "20px",
                    py: "18px",
                    ...figmaFontStyles.merriweather700,
                    fontSize: "14px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    "&:hover": {
                      bgcolor: "#DF7468",
                      opacity: 0.9,
                    },
                  }}
                >
                  {finalCtaContent.ctaText}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#F2B994" }}>
            <Container maxWidth="lg">
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "90%", sm: "70%", md: "600px" },
                  mx: "auto",
                  mb: 6,
                  textAlign: "center",
                }}
              >
                <img
                  src={resumeLibraryContent.illustration}
                  alt="Resume Samples Illustration"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    maxWidth: "600px",
                    margin: "0 auto",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  ...figmaFontStyles.merriweather700,
                  fontSize: { xs: "2.5rem", md: "60px" },
                  lineHeight: 1.167,
                  color: "#333333",
                  textAlign: "center",
                  mb: 3,
                }}
              >
                {resumeLibraryContent.title}
              </Typography>

              <Typography
                sx={{
                  ...figmaFontStyles.poppins400,
                  fontSize: { xs: "1rem", md: "20px" },
                  lineHeight: 1.5,
                  color: "#000000",
                  textAlign: "center",
                  maxWidth: "700px",
                  mx: "auto",
                  mb: 5,
                }}
              >
                {resumeLibraryContent.description}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "25px",
                  justifyContent: "center",
                  maxWidth: "1000px",
                  mx: "auto",
                }}
              >
                {resumeLibraryContent.tags.map((tag, index) =>
                  renderTag(tag, resumeLibraryContent.icon, index)
                )}
              </Box>
            </Container>
          </Box>

          <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#F1F5F8" }}>
            <Container maxWidth="lg">
              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box sx={{ textAlign: "center" }}>
                    <img
                      src={careerResourcesContent.illustration}
                      alt="Career Resources Illustration"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{
                      ...figmaFontStyles.merriweather700,
                      fontSize: { xs: "2.8rem", md: "60px" },
                      lineHeight: 1.167,
                      color: "#333333",
                      mb: 3,
                    }}
                  >
                    {careerResourcesContent.titleLine1}
                    <br />
                    {careerResourcesContent.titleLine2}
                  </Typography>
                  <Typography
                    sx={{
                      ...figmaFontStyles.poppins400,
                      fontSize: "20px",
                      lineHeight: 1.5,
                      color: "#000000",
                      mb: 4,
                      maxWidth: "500px",
                    }}
                  >
                    {careerResourcesContent.description}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                    {careerResourcesContent.tags.map((tag, index) =>
                      renderMetric(tag.label, tag.icon, index)
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Report image section */}
          <Box
            sx={{
              minHeight: { xs: "auto", md: "calc(100vh - 64px)" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background:
                "linear-gradient(135deg, #F590AB 0%, #F2A442 60%, #DF7468 100%)",
              py: { xs: 6, md: 8 },
              textAlign: "center",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Container maxWidth="md">
              <Typography
                component="h1"
                sx={{
                  fontFamily: "Merriweather, serif",
                  fontWeight: 700,
                  fontSize: { xs: "2.8rem", sm: "3.5rem", md: "60px" },
                  lineHeight: 1.167,
                  color: "#FFFFFF",
                  mb: 3,
                }}
              >
                {heroContent.titleLine1}
                <br />
                {heroContent.titleLine2}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 400,
                  fontSize: { xs: "1rem", md: "20px" },
                  lineHeight: 1.5,
                  color: "#FFFFFF",
                  mb: 4,
                  maxWidth: "700px",
                  mx: "auto",
                }}
              >
                {heroContent.description}
              </Typography>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <SquigglyArrow sx={{ top: 10, left: -90 }} />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStart}
                  disableElevation
                  startIcon={
                    <img
                      src={heroContent.ctaIcon}
                      alt=""
                      style={{ height: "14px", width: "auto" }}
                    />
                  }
                  sx={{
                    bgcolor: "#FDE470",
                    color: "#333333",
                    borderRadius: "5px",
                    px: "20px",
                    py: "18px",
                    fontFamily: "Merriweather, serif",
                    fontWeight: 700,
                    fontSize: "14px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    "&:hover": {
                      bgcolor: "#FDE470",
                      opacity: 0.9,
                    },
                    mb: { xs: 4, md: 6 },
                  }}
                >
                  {heroContent.ctaText}
                </Button>
              </Box>
            </Container>

            <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
              <Box
                sx={{
                  maxWidth: "1000px",
                  width: "100%",
                  mx: "auto",
                  mt: { xs: 4, md: 2 },
                }}
              >
                <img
                  src={heroContent.imageOverview}
                  alt="CoachCarol Banner Header"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    maxWidth: "1000px",
                    borderRadius: "16px",

                    margin: "0 auto",
                  }}
                />
              </Box>
            </Container>
          </Box>

          {/* Animals Section with CTA */}
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 10, md: 15 },
              bgcolor: "#FDE36F",
            }}
          >
            <Container maxWidth="lg">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 6,
                  px: 2,
                }}
              >
                <img
                  src={finalCtaContent.animalIllustration}
                  alt="Career Animals Illustration"
                  style={{
                    height: "auto",
                    width: "100%",
                    maxWidth: "600px",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  ...figmaFontStyles.merriweather700,
                  fontSize: { xs: "2.5rem", md: "60px" },
                  lineHeight: 1.167,
                  color: "#333333",
                  textAlign: "center",
                  mb: 3,
                }}
              >
                {finalCtaContent.title}
              </Typography>
              <Typography
                sx={{
                  ...figmaFontStyles.poppins400,
                  fontSize: { xs: "1rem", md: "20px" },
                  lineHeight: 1.5,
                  color: "#000000",
                  textAlign: "center",
                  maxWidth: "700px",
                  mx: "auto",
                  mb: 4,
                }}
              >
                {finalCtaContent.description}
              </Typography>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <SquigglyArrow sx={{ top: 10, left: -90 }} />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStart}
                  disableElevation
                  startIcon={
                    <img
                      src={finalCtaContent.ctaIcon}
                      alt=""
                      style={{ height: "14px" }}
                    />
                  }
                  sx={{
                    bgcolor: "#DF7468",
                    color: "#FFFFFF",
                    borderRadius: "5px",
                    px: "20px",
                    py: "18px",
                    ...figmaFontStyles.merriweather700,
                    fontSize: "14px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    "&:hover": {
                      bgcolor: "#DF7468",
                      opacity: 0.9,
                    },
                  }}
                >
                  {finalCtaContent.ctaText}
                </Button>
              </Box>
            </Container>
          </Box>
        </>
      )}
    </>
  );
};

export default LandingPage;
